import React, { useState, useEffect, useRef, useMemo, ComponentProps } from 'react';
import { round } from 'lodash';
import { Map, MapboxEvent, MapRef, NavigationControl, ViewState } from 'react-map-gl';
import { Box, BoxProps } from '@opendoor/bricks/core';
import { useObservability } from 'shared/helpers/observability';

import { BallparkComp } from 'growth/graphql/useOfferComps';
import { HomeMarker } from 'shared/components/map/HomeMarker';
import { Marker } from 'shared/components/map/Marker';
import { MAPBOX_ACCESS_TOKEN, MAPBOX_MAP_STYLE } from 'shared/helpers/globals';
import { useWindowResize } from 'shared/helpers/hooks/useWindowResize';
import { useDebouncedCallback } from 'use-debounce/lib';

import 'mapbox-gl/dist/mapbox-gl.css';

type CompsMapProps = {
  comps: BallparkComp[];
  subject: { latitude: number; longitude: number };
  onClickMarker: (index: number | undefined) => void;
  selectedMarker?: number;
  freezeMap?: boolean;
  onMapLoad?: (e: MapboxEvent) => void;
};

/**
 * TODO: Add documentation
 *
 * component-test-check-ignore
 */
export const CompsMap: React.FC<CompsMapProps & BoxProps> = ({
  comps,
  subject,
  selectedMarker,
  freezeMap,
  onClickMarker,
  onMapLoad,
  ...props
}) => {
  const zoom = 13;
  const longitude = subject.longitude;
  const latitude = subject.latitude;
  const [viewport, setViewport] = useState<Partial<ViewState>>({
    latitude,
    longitude,
    zoom,
  });
  const { trackEvent } = useObservability();
  const baseDiv = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (selectedMarker) {
      const comp = comps[selectedMarker];
      if (comp?.address?.longitude && comp?.address?.latitude) {
        setViewport({
          ...viewport,
          zoom: 15,
          longitude: parseFloat(comp.address.longitude),
          latitude: parseFloat(comp.address.latitude),
        });
      }
    }
    // eslint-disable-next-line
  }, [selectedMarker]);

  const commonChildren = (
    <>
      <HomeMarker
        latitude={latitude}
        longitude={longitude}
        onClick={() => {
          trackEvent('click', 'offer-comp-home-marker');
          setViewport({
            ...viewport,
            longitude,
            latitude,
          });
        }}
      />
      <CompsMarkers
        comps={comps}
        onClickMarker={(index) => {
          trackEvent('click', 'offer-comp-marker');
          onClickMarker(index);
        }}
        selectedMarker={selectedMarker}
      />
    </>
  );

  // Mapbox normally handles resizing itself, but there's a race condition
  // between their code and our Layout component updating when the user
  // crosses breakpoints, so we need to force the resize ourselves
  const mapRef = useRef<MapRef | null>(null);
  const debouncedResizeMap = useDebouncedCallback(() => mapRef.current?.getMap().resize(), 300);
  useWindowResize(debouncedResizeMap);

  const commonProps: ComponentProps<typeof Map> = {
    mapboxAccessToken: MAPBOX_ACCESS_TOKEN,
    mapStyle: MAPBOX_MAP_STYLE,
    ...viewport,
    onLoad: onMapLoad,
  };

  return (
    <Box
      width="100%"
      height="100%"
      ref={(div: HTMLDivElement) => (baseDiv.current = div)}
      {...props}
      data-testid="comps-map"
      isolation="isolate"
      zIndex={0}
    >
      {freezeMap ? (
        <Map ref={mapRef} {...commonProps} interactive={false}>
          {commonChildren}
        </Map>
      ) : (
        <Map ref={mapRef} onMove={(e) => setViewport(e.viewState)} {...commonProps}>
          {commonChildren}
          <NavigationControl position="top-left" showCompass={false} />
        </Map>
      )}
    </Box>
  );
};

interface ICompsMarkers {
  comps: BallparkComp[];
  onClickMarker?: (index: number | undefined) => void;
  selectedMarker?: number;
}

const CompsMarkers: React.FC<ICompsMarkers> = ({ comps, onClickMarker, selectedMarker }) => {
  // only rerender markers if props.comps has changed
  const markers = useMemo(
    () =>
      comps?.map((comp, index) => {
        if (comp?.address?.latitude && comp?.address?.longitude) {
          const listing = comp.listing;
          const price = listing?.closeDate ? listing?.closePrice : listing?.listPrice;
          const priceFormatted = price && `$${round(price / 1000)}K`;
          return (
            <Marker
              id={comp.id}
              key={comp.id}
              content={priceFormatted || ''}
              index={index}
              onClick={onClickMarker}
              selected={selectedMarker === index}
              longitude={parseFloat(comp.address.longitude)}
              latitude={parseFloat(comp.address.latitude)}
            />
          );
        }
      }),
    // eslint-disable-next-line
    [comps, selectedMarker],
  );

  return <>{markers}</>;
};
